// Content of this file was copied from `apps/website/sentry.client.config.ts`
// Read more: https://docs.sentry.io/platforms/javascript/guides/nextjs/manual-setup/#initialize-sentry-sdks

;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"lead-form@2.57.11"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryNextJsVersion"] = "14.2.24";globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/website/v2/live";globalThis["_sentryAssetPrefix"] = "https://static.smile2impress.com/website/v2/live";globalThis["_sentryExperimentalThirdPartyOriginStackFrames"] = undefined;globalThis["_sentryRouteManifest"] = "{\"isrRoutes\":[],\"dynamicRoutes\":[],\"staticRoutes\":[]}";import {
    init,
    setTag,
    getCurrentScope,
    captureRouterTransitionStart,
} from "@sentry/nextjs";
import type { Event, StackFrame, Breadcrumb, ErrorEvent } from "@sentry/nextjs";

import { getAppMode } from "./utils/getAppMode";
import supportedBrowsers from "../supportedBrowsers";

const { isLiveMode } = getAppMode();

const ERROR_ABOUT_CURRENT_TARGET =
    "Non-Error promise rejection captured with keys: currentTarget, isTrusted, target, type";

const ERROR_ABOUT_ACCESS_COOKIE =
    "Failed to read the 'cookie' property from 'Document': Access is denied for this document.";

const ERROR_ABOUT_TOKENS = "Unexpected token";
const ERROR_PIXEL = "Pixel";
const ERROR_ABOUT_CHROME_EXTENSIONS_VIDEO = "ibFindAllVideos";
const ERROR_UNKNOWN_NO_RESPONSE = "Unknown error with no response";

const baseDenyUrls = [
    /graph\.facebook\.com/i,
    /connect\.facebook\.net\/en_US\/all\.js/i,
    /js\.hubspot\.com\/web-interactives-embed\.js/i,
    /extensions\//i,
    /^chrome:\/\//i,
];

const filteredUrls = ["/terminvereinbarung.html"];

const thirdPartyServicesUrls = [
    "ct.pinterest.com",
    "smooch-website-chatbot.herokuapp.com/wa-msg-me-btn",
    "cta-service-cms2.hubspot.com",
    "conversions-config.reddit.com",
    "redditstatic.com/ads",
];

const ignoreErrors = [
    // issues about it https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/40
    "Non-Error exception captured",
    "Non-Error promise rejection captured",

    // webkit bug  issue - https://bugs.webkit.org/show_bug.cgi?id=246010
    "Error response received for message <page-ready>",

    // hubspot
    "Cannot read properties of null (reading 'portalId')",

    // webviews
    /^No error$/,
    /__show__deepen/,
    /_avast_submit/,
    /Access is denied/,
    /anonymous function: captureException/,
    /Blocked a frame with origin/,
    /can't redefine non-configurable property "userAgent"/,
    /change_ua/,
    /console is not defined/,
    /cordova/,
    /DataCloneError/,
    /Error: AccessDeny/,
    /event is not defined/,
    /feedConf/,
    /ibFindAllVideos/,
    /myGloFrameList/,
    /SecurityError/,
    /MyIPhoneApp/,
    /snapchat.com/,
    /vid_mate_check is not defined/,
    /win\.document\.body/,
    /window\._sharedData\.entry_data/,
    /window\.regainData/,
    /ztePageScrollModule/,
];

const ignoreExtendsScripts = [
    "gtm",
    "pixel",
    "whatsapp",
    "gtag",
    "configFetcher",
    "google",
    "chrome-extension",
    "fb",
];

const getPreContext = (element: StackFrame) =>
    element.pre_context && JSON.stringify(element.pre_context);

const getPostContext = (element: StackFrame) =>
    element.post_context && JSON.stringify(element.post_context);

const hasIgnoreScripts = (element: StackFrame, ignoreScripts: string[]) => {
    return Boolean(
        ignoreScripts.find((script) => element.module?.includes(script))
    );
};

const isStackTraceError = (event: Event) => {
    const exception = event.exception;

    return (
        exception?.values &&
        exception?.values[0].stacktrace?.frames?.find((element) => {
            const postText = getPostContext(element);

            return (
                getPreContext(element)?.includes("chakra") ||
                postText?.includes("google") ||
                postText?.includes("iframe") ||
                hasIgnoreScripts(element, ignoreExtendsScripts) ||
                // core libs
                element.module?.includes("react-dom") ||
                // sentry SDK
                element.filename === "[native code]"
            );
        })
    );
};

const isValueError = (event: Event) => {
    const values = event.exception?.values;

    if (values?.[0].value) {
        const value = values[0].value;

        return (
            // issue aboit it https://github.com/getsentry/sentry-javascript/issues/2546
            value === ERROR_ABOUT_CURRENT_TARGET ||
            value === ERROR_ABOUT_ACCESS_COOKIE ||
            value.includes(ERROR_PIXEL) ||
            value.includes(ERROR_ABOUT_TOKENS) ||
            value.includes(ERROR_ABOUT_CHROME_EXTENSIONS_VIDEO) ||
            event.message === ERROR_UNKNOWN_NO_RESPONSE
        );
    } else {
        return false;
    }
};

const shouldFilterEvent = (event: Event) => {
    if (event.request && event.request.url) {
        const requestUrl = event.request.url;

        return filteredUrls.some((url) => requestUrl.includes(url));
    }

    return false;
};

const shouldIgnoreThirdParty = (error: ErrorEvent) => {
    return isLoadFailedError(error);
};

const isLoadFailedError = (error: ErrorEvent) => {
    const now = Date.now();

    if (!error?.breadcrumbs) {
        return false;
    }

    // Check last 10 breadcrumbs
    for (
        let i = error.breadcrumbs.length - 1;
        i >= error.breadcrumbs.length - 10 && i >= 0;
        i--
    ) {
        const breadcrumb = error.breadcrumbs[i];
        if (!breadcrumb) continue;

        // Only fresh breadcrumbs, no more than 2 seconds
        if (breadcrumb.timestamp && now - breadcrumb.timestamp * 1000 > 2000) {
            break;
        }
        if (isBreadcrumbHasThirdPartyScript(breadcrumb)) {
            return true;
        }
    }

    return false;
};

const isBreadcrumbHasThirdPartyScript = (breadcrumb: Breadcrumb) => {
    if (!["fetch", "xhr"].includes(breadcrumb.category || "")) {
        return false;
    }
    const url: string = (breadcrumb.data?.url as string) || "";
    return thirdPartyServicesUrls.some((tpUrl) => url.includes(tpUrl));
};

// Filter out non-actionable browser/WebView errors that are common in mobile WebViews
// These errors are typically caused by browser extensions, injected scripts, or WebView-specific
// JavaScript bridge communication issues that don't represent actual application errors
const browserWebViewErrorPatterns = [
    /window\.webkit\.messageHandlers/i,
    /Java object is gone/i,
];

const isBrowserWebViewError = (event: Event): boolean => {
    // Helper to check if text matches any pattern
    const matchesPattern = (text: string): boolean => {
        return browserWebViewErrorPatterns.some((pattern) =>
            pattern.test(text)
        );
    };

    // Check message field
    if (event.message) {
        const message = String(event.message);
        if (matchesPattern(message)) {
            return true;
        }
    }

    // Check exception values
    const exception = event.exception;
    if (exception?.values) {
        for (const exceptionValue of exception.values) {
            // Check value field
            if (exceptionValue.value) {
                const value = String(exceptionValue.value);
                if (matchesPattern(value)) {
                    return true;
                }
            }
        }
    }

    return false;
};

if (isLiveMode) {
    init({
        dsn: process.env.SENTRY_DSN,
        release: process.env.RELEASE_VERSION,
        tracesSampleRate: 0.1,
        ignoreErrors,
        denyUrls: baseDenyUrls,
        ignoreTransactions: ["health-check", "healthcheck"],
        beforeSendTransaction(event) {
            if (event.transaction?.includes("healthcheck")) {
                return null;
            }

            return event;
        },
        beforeSend: (event) => {
            if (!supportedBrowsers.test(navigator.userAgent)) return null;

            if (shouldFilterEvent(event)) return null;

            if (isStackTraceError(event) || isValueError(event)) return null;

            if (shouldIgnoreThirdParty(event)) return null;

            // Filter out non-actionable browser/WebView errors
            if (isBrowserWebViewError(event)) return null;

            return event;
        },
    });

    setTag("new_app", true);
    getCurrentScope().setTag("layer", "frontend");
}

export const onRouterTransitionStart = captureRouterTransitionStart;
